import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "@supabase/auth-helpers-react";
import { Button } from "@/components/ui/button";
import { ThumbsUp, Share2 } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

interface ToolCardActionsProps {
  id: string;
  name: string;
  description: string;
  likeCount: number;
  hasLiked: boolean;
  onLike: () => void;
}

export const ToolCardActions = ({ 
  id, 
  name, 
  description, 
  likeCount, 
  hasLiked, 
  onLike 
}: ToolCardActionsProps) => {
  const user = useUser();
  const navigate = useNavigate();

  const handleShare = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    const toolUrl = `${window.location.origin}/tool/${id}`;
    
    try {
      if (navigator.share) {
        await navigator.share({
          title: name,
          text: description,
          url: toolUrl,
        });
      } else {
        await navigator.clipboard.writeText(toolUrl);
        toast.success("تم نسخ الرابط بنجاح");
      }
    } catch (error) {
      if (error instanceof Error && error.name !== "AbortError") {
        toast.error("حدث خطأ أثناء المشاركة");
      }
    }
  }, [id, name, description]);

  const handleLike = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) {
      toast.error("يجب تسجيل الدخول أولاً للتصويت");
      navigate("/auth");
      return;
    }
    onLike();
  }, [user, navigate, onLike]);

  return (
    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
      <Button
        variant="ghost"
        size="sm"
        className="relative group flex items-center gap-2"
        onClick={handleLike}
      >
        <ThumbsUp
          className={`w-4 h-4 transition-all duration-300 ${
            hasLiked ? "text-primary fill-primary scale-110" : "group-hover:scale-110"
          }`}
        />
        <motion.span
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-sm text-muted-foreground"
        >
          {likeCount}
        </motion.span>
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={handleShare}
        className="group"
      >
        <Share2 className="w-4 h-4 transition-transform group-hover:scale-110" />
      </Button>
    </div>
  );
};